/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useCallback } from "react";
import ReactECharts from "src/utils/EChartsReact";
import { getCommonTooltipConfig, buildChartTooltip, useTooltipCoordination } from "../tooltipFormatter";
import { hasAnyStatMarkers, resolveStatMarkerRows, buildVStackedMarkPoints } from "./../../components/StatMarkerUtils";
import { formatNumber, convertStringToNumeric } from "src/pages/Insights/transformer/dashboardUtils";
import { chartConfig } from "../../chartConfig";
import { IStatMarker, IStatTestConfig } from "src/pages/Insights/models/interfaces";
import { useScaledConfig } from "../../ChartScaleContext";
import { isLightColor, getNSizeStatusLabel, NSizeLabelText, SUPPRESSED_VALUE_PLACEHOLDER, SUPPRESSED_VALUE_COLOR } from "../chartHelpers";

interface IMiniVStackedBarChartProps {
	series: {
		name: string;
		value: number;
		n_size: number | string;
		stat_marker?: IStatMarker | null;
	}[];
	colors: Record<string, string>;
	width: number;
	height?: number;
	labelFormat: string;
	yAxisDomain: { min: number; max: number };
	top?: number;
	bottom?: number;
	containerWidth?: number;
	statTestConfig?: IStatTestConfig | null;
	dataLabelFontSize?: number;
}

const MiniVStackedBarChart: React.FC<IMiniVStackedBarChartProps> = ({
	series,
	colors,
	width,
	height = 280,
	labelFormat,
	yAxisDomain,
	top = 0,
	bottom = 0,
	statTestConfig,
	containerWidth,
	dataLabelFontSize,
}) => {
	// Extract and memoize config values once
	const scaled = useScaledConfig();
	const { chartRef, onEvents } = useTooltipCoordination();
	const config = useMemo(() => {
		const typographyConfig = chartConfig.getTypographyConfig();
		const vStackedConfig = chartConfig.getVerticalStackedConfig();
		const colorsConfig = chartConfig.getColorsConfig();
		return {
			barWidthPercent: vStackedConfig.barWidthPercent,
			minBarWidth: vStackedConfig.minBarWidth,
			maxBarWidth: Math.min(vStackedConfig.maxBarWidth, scaled.geometry.maxVStackedBarWidth),
			// Use scaled dataLabelFontSize as the primary label size
			labelFontSize: dataLabelFontSize ?? typographyConfig.fontSize.label,
			dataLabelCompact: dataLabelFontSize ? Math.max(dataLabelFontSize - 1, typographyConfig.fontSize.dataLabelCompact) : typographyConfig.fontSize.dataLabelCompact,
			whiteBg: colorsConfig.backgrounds.white,
			borderGray: colorsConfig.borders.gray,
			mutedText: colorsConfig.text.muted,
			statMarkerLayout: vStackedConfig.statMarkerLayout,
		};
	}, [dataLabelFontSize, scaled.geometry.maxVStackedBarWidth]);

	// Memoize bar width calculations
	const barWidthConfig = useMemo(() => {
		const calculatedBarWidth = Math.floor(width * config.barWidthPercent);
		const barWidth = Math.min(Math.max(calculatedBarWidth, config.minBarWidth), config.maxBarWidth);

		// Adjust label requirements based on bar width
		const MIN_LABEL_HEIGHT = barWidth < 25 ? 18 : 14;
		const labelFontSize = barWidth < 20 ? config.dataLabelCompact : barWidth < 30 ? config.dataLabelCompact : config.labelFontSize;
		const showLabels = barWidth >= 16;

		return {
			barWidth,
			MIN_LABEL_HEIGHT,
			labelFontSize,
			showLabels,
		};
	}, [width, config]);

	// Memoize tooltip formatter with stat marker support
	const tooltipFormatter = useCallback((params: any) => buildChartTooltip(params, series, labelFormat, statTestConfig, colors), [series, labelFormat, statTestConfig, colors]);

	// Check if any series has stat markers
	const hasAnyStatMarker = useMemo(() => hasAnyStatMarkers(series, statTestConfig), [series, statTestConfig]);

	const dataStatus = useMemo(() => {
		if (!series || series.length === 0) return "no-data";

		const allValuesAreNoData = series.every((s) => {
			const val = s.value;
			return val === undefined || val === null || (typeof val === "string" && (val as string).toLowerCase() === "no data") || (typeof val === "number" && !isFinite(val));
		});

		if (allValuesAreNoData) return "no-data";

		// All segments have suppressed n_size (No data or Low N) — none renders.
		const statuses = series.map((s) => getNSizeStatusLabel(s.n_size));
		if (statuses.every((st) => st !== null)) {
			return statuses.some((st) => st === NSizeLabelText.LowN) ? "low-n" : "no-data";
		}

		const totalValue = series.reduce((sum, s) => {
			const val = typeof s.value === "number" && isFinite(s.value) ? s.value : 0;
			return sum + val;
		}, 0);

		if (totalValue === 0) return "zero-sum";
		return "valid";
	}, [series]);

	const echartsSeries = useMemo(() => {
		const isPercentage = labelFormat.includes("%");
		const { barWidth, MIN_LABEL_HEIGHT, labelFontSize, showLabels } = barWidthConfig;

		let cumulativeValue = 0;

		return series.map((s) => {
			const isNoDataValue =
				s.value === undefined ||
				s.value === null ||
				(typeof s.value === "string" && (s.value as string).toLowerCase() === "no data") ||
				(typeof s.value === "number" && !isFinite(s.value));

			const nSizeStatusLabel = getNSizeStatusLabel(s.n_size);
			const isNoDataNSize = nSizeStatusLabel === NSizeLabelText.NoData;
			const suppressValue = nSizeStatusLabel !== null;
			const treatAsNoData = isNoDataValue || suppressValue;

			const processedValue = isPercentage && !treatAsNoData ? s.value * 100 : treatAsNoData ? 0 : s.value;

			// Each segment's pixel height is simply proportional to its own value
			const valueRange = yAxisDomain.max - yAxisDomain.min;
			const segmentPixelHeight = valueRange !== 0 && processedValue !== 0 ? (Math.abs(processedValue) / valueRange) * height : 0;

			const formattedVal = treatAsNoData ? SUPPRESSED_VALUE_PLACEHOLDER : formatNumber(s.value, labelFormat);
			const value = treatAsNoData ? 0 : convertStringToNumeric(formattedVal);

			// Use value (what ECharts actually stacks) for midpoint calculation
			const segmentBottom = cumulativeValue;
			cumulativeValue += processedValue; // NOT the display-rounded `value`
			const segmentMidY = (segmentBottom + cumulativeValue) / 2;

			// --- Unified height-based display decision ---
			const GAP = 2;
			const MARKER_FONT_SIZE = 7;
			const labelRowHeight = labelFontSize + GAP;
			const markerRowHeight = MARKER_FONT_SIZE + GAP;

			// Height thresholds
			const heightForLabelOnly = labelRowHeight + GAP;
			const heightForLabelAndMarker = labelRowHeight + markerRowHeight + GAP * 2;
			const heightForMarkerOnly = markerRowHeight + GAP;

			// Resolve stat marker rows — gated only on: not no-data, config enabled
			const resolvedRows = !treatAsNoData && statTestConfig && config.statMarkerLayout ? resolveStatMarkerRows(s.stat_marker, statTestConfig) : [];

			const hasMarkers = resolvedRows.length > 0;

			// Determine what fits in this segment
			const canFitLabel = !treatAsNoData && showLabels && segmentPixelHeight >= heightForLabelOnly;
			const canFitLabelAndMarker = canFitLabel && hasMarkers && segmentPixelHeight >= heightForLabelAndMarker;
			const canFitMarkerOnly = hasMarkers && segmentPixelHeight >= heightForMarkerOnly;

			// Decision matrix:
			// 1. Label + Marker  → both fit
			// 2. Label only      → label fits, marker doesn't (or no markers)
			// 3. Marker only     → marker fits but label doesn't → show asterisk, hide label
			// 4. Nothing         → segment too small
			const showLabel = canFitLabelAndMarker || (canFitLabel && !hasMarkers);
			const showMarker = canFitLabelAndMarker || (!canFitLabel && canFitMarkerOnly);

			// Calculate label half-width for marker offset
			// Replace the labelText line used for estimatedLabelWidth
			const labelText = treatAsNoData ? SUPPRESSED_VALUE_PLACEHOLDER : formattedVal;
			const estimatedLabelWidth = labelText.length * (labelFontSize * 0.65);
			const labelHalfWidth = Math.ceil(estimatedLabelWidth / 2);

			// Build markPoint only if we decided to show markers
			const markerResult =
				showMarker && resolvedRows.length > 0
					? buildVStackedMarkPoints(resolvedRows, statTestConfig!, segmentMidY, labelHalfWidth, width, barWidthConfig.barWidth, segmentPixelHeight, labelFontSize)
					: { markPointData: [], hideLabel: false };

			const { markPointData } = markerResult;

			markPointData.forEach((pt: any) => {
				pt.dataPointMeta = {
					stat_marker: s.stat_marker ?? null,
					value: s.value,
					n_size: s.n_size,
					name: s.name,
				};
			});

			// Final label visibility — driven entirely by the unified check above
			const finalShowLabel = showLabel;

			// Determine text color based on background color contrast
			const bgColor = colors[s.name];
			const isLightBg = bgColor ? isLightColor(bgColor) : false;
			const textColor = isLightBg ? "#000" : config.whiteBg;

			return {
				name: s.name,
				type: "bar" as const,
				stack: "total",
				data: [
					{
						value,
						n_size: isNoDataNSize ? NSizeLabelText.NoData : s.n_size,
						name: s.name,
						displayValue: treatAsNoData ? (nSizeStatusLabel ?? NSizeLabelText.NoData) : formattedVal,
						stat_marker: s.stat_marker ?? null,
					},
				],
				itemStyle: {
					color: colors[s.name],
					borderColor: config.whiteBg,
					borderWidth: 0,
				},
				label: {
					show: finalShowLabel,
					position: "inside" as const,
					fontSize: labelFontSize,
					fontWeight: "normal" as const,
					// Use pre-formatted value that respects dlabel_fmt (e.g. "26.00%")
					formatter: () => formattedVal,
					color: treatAsNoData ? SUPPRESSED_VALUE_COLOR : textColor,
				},
				barWidth: barWidth,
				barMinWidth: barWidth,
				barMaxWidth: barWidth,
				...(markPointData.length > 0
					? {
						markPoint: {
							symbol: "circle",
							symbolSize: 0,
							data: markPointData,
							animation: false,
							tooltip: { show: true },
						},
					}
					: {}),
			};
		});
	}, [series, colors, labelFormat, yAxisDomain.min, yAxisDomain.max, height, barWidthConfig, config, statTestConfig]);

	// Memoize complete option object
	const option = useMemo(
		() => ({
			animation: false,
			grid: {
				left: 0,
				right: 0,
				top,
				bottom,
				containLabel: false,
			},
			tooltip: {
				...getCommonTooltipConfig(),
				formatter: tooltipFormatter,
				renderMode: "html",
				className: "stat-marker-tooltip",
			},
			xAxis: {
				type: "category" as const,
				data: ["Wave"],
				show: false,
				axisLine: { show: false },
				axisTick: { show: false },
			},
			yAxis: {
				type: "value" as const,
				min: yAxisDomain.min,
				max: yAxisDomain.max,
				show: false,
				splitLine: { show: false },
				axisLine: {
					show: true,
					onZero: true,
					lineStyle: {
						color: config.borderGray,
						width: 1,
					},
				},
			},
			series: echartsSeries,
		}),
		[echartsSeries, tooltipFormatter, yAxisDomain.min, yAxisDomain.max, config, top, hasAnyStatMarker],
	);

	if (dataStatus === "no-data" || dataStatus === "low-n") {
		return (
			<div className="flex items-end  justify-center mb-2" style={{ width, height }}>
				<span className="text-xs" style={{ color: SUPPRESSED_VALUE_COLOR }}>
					{dataStatus === "low-n" ? NSizeLabelText.LowN : SUPPRESSED_VALUE_PLACEHOLDER}
				</span>
			</div>
		);
	}

	return <ReactECharts ref={chartRef} onEvents={onEvents} option={option} style={{ width: "100%", height }} autoResize opts={{ renderer: "svg" }} />;
};

// Custom comparison for proper memoization
export default React.memo(MiniVStackedBarChart, (prevProps, nextProps) => {
	return (
		prevProps.series === nextProps.series &&
		prevProps.colors === nextProps.colors &&
		prevProps.width === nextProps.width &&
		prevProps.height === nextProps.height &&
		prevProps.labelFormat === nextProps.labelFormat &&
		prevProps.yAxisDomain.min === nextProps.yAxisDomain.min &&
		prevProps.yAxisDomain.max === nextProps.yAxisDomain.max &&
		prevProps.top === nextProps.top &&
		prevProps.statTestConfig === nextProps.statTestConfig &&
		prevProps.dataLabelFontSize === nextProps.dataLabelFontSize
	);
});